import React, { useState, useEffect, useRef, useMemo } from 'react';
import * as XLSX from 'xlsx';
import {
  Download, FileSpreadsheet, MapPin, Users,
  CheckCircle2, AlertTriangle, Loader2, X, Play, Info, RefreshCw,
} from 'lucide-react';
import {
  getWorkplaces, createWorkplace, addWorker, getCompanyWorkers, searchWorkers, listAgreements,
  saveWeekSchedule, DAYS_OF_WEEK,
} from '../services/api';
import { USER_TYPE_LABELS } from '../lib/permissions';
import { PASSWORD_MIN_LENGTH } from '../lib/passwordStrength';
import { readSpreadsheetFile, ACCEPT_ATTR, delimiterLabel } from '../utils/spreadsheetFile';
import { agreementsForCompany } from '../utils/agreements';
import {
  norm, SEDE_HEADERS_ES, SCHEDULE_HEADERS_ES, WORKER_HEADERS_ES,
  rowsToSedes, rowsToWorkers, toBackendDay, diagnoseHeaders,
  indexWorkplaces, sedeKey, findUnknownSedes, unresolvedSedes, resolveWorkerSede,
  findDuplicates, existingMatch,
} from '../utils/bulkImportParsing';
import { useToast } from './Toast';
import { FEEDBACK } from '../services/feedbackMessages';

// La lógica pura (cabeceras, parseo de filas, diagnóstico, sedes y
// duplicados) vive en utils/bulkImportParsing.js y la lectura de archivos
// (xlsx/xls/csv/tsv/txt) en utils/spreadsheetFile.js — ambas con tests.

const SAMPLE_PASSWORDS = ['Worker123!', 'Admin123!', 'Sede1234!'];
const PASSWORD_POLICY_TEXT = `mínimo ${PASSWORD_MIN_LENGTH} caracteres con mayúscula, minúscula, número y símbolo`;
const FILE_FORMATS_TEXT = 'Formatos admitidos: Excel (.xlsx, .xls) o texto CSV/TSV (.csv, .tsv, .txt) con las MISMAS cabeceras, en UTF-8 o Windows-1252 y separado por tabulador, punto y coma o coma.';

// ─────────────────────────────────────────────────────────────
// Plantillas
// ─────────────────────────────────────────────────────────────
const downloadSedesTemplate = (companyName) => {
  const wb = XLSX.utils.book_new();

  const slug = (companyName || 'empresa').toLowerCase().replace(/\s+/g, '');
  const sample = [
    [`Sede ${companyName} Centro`, `centro@${slug}.com`, '+34900000001', 'SÍ'],
    [`Sede ${companyName} Norte`,  `norte@${slug}.com`,  '+34900000002', 'SÍ'],
  ];
  const ws = XLSX.utils.aoa_to_sheet([SEDE_HEADERS_ES, ...sample]);
  ws['!cols'] = SEDE_HEADERS_ES.map(() => ({ wch: 28 }));
  XLSX.utils.book_append_sheet(wb, ws, 'Sedes');

  const instr = [
    [`Plantilla de SEDES — ${companyName}`],
    [],
    ['Cabeceras (no las renombres)'],
    ...SEDE_HEADERS_ES.map((h) => [`  · ${h}`]),
    [],
    ['Notas'],
    ['  · Nombre de la sede es obligatorio.'],
    ['  · Email y teléfono son opcionales.'],
    ['  · Login habilitado: escribe SÍ o NO. Por defecto SÍ.'],
    ['  · Las sedes ya existentes con el mismo nombre se omitirán al importar.'],
    [`  · ${FILE_FORMATS_TEXT}`],
  ];
  const wsI = XLSX.utils.aoa_to_sheet(instr);
  wsI['!cols'] = [{ wch: 80 }];
  XLSX.utils.book_append_sheet(wb, wsI, 'Instrucciones');

  const safe = (companyName || 'empresa').replace(/[^A-Za-z0-9_-]+/g, '_');
  XLSX.writeFile(wb, `plantilla_sedes_${safe}.xlsx`);
};

// "HH:MM" → fracción de día (formato nativo de una celda "Hora" de Excel).
const hourToExcelFraction = (hhmm) => {
  if (!hhmm) return '';
  const m = String(hhmm).match(/^(\d{1,2}):(\d{2})$/);
  if (!m) return '';
  return (Number(m[1]) * 60 + Number(m[2])) / 1440;
};

// { lunes: [in1,out1,in2,out2], ... } → 28 valores (fracción de día o '').
const scheduleCells = (perDay) =>
  DAYS_OF_WEEK.flatMap(({ label }) => {
    const arr = perDay[label.toLowerCase()] || ['', '', '', ''];
    return arr.map(hourToExcelFraction);
  });

const downloadWorkersTemplate = (companyName, agreements = [], workplaces = []) => {
  const wb = XLSX.utils.book_new();
  const exampleAgreement = agreements[0]?.name || '';
  const wpNames = (workplaces || []).map((w) => w?.name).filter(Boolean);
  // Las filas de ejemplo usan sedes REALES de la empresa si las hay, para
  // que la plantilla se pueda importar tal cual sin "sede no encontrada".
  const sedeA = wpNames[0] || `Sede ${companyName} Centro`;
  const sedeB = wpNames[1] || wpNames[0] || `Sede ${companyName} Norte`;

  // Trabajador típico: L-V + sábado reducido, domingo descanso.
  const t1Sched = scheduleCells({
    lunes:     ['09:00', '17:00', '', ''],
    martes:    ['09:00', '17:00', '', ''],
    miércoles: ['09:00', '17:00', '', ''],
    jueves:    ['09:00', '17:00', '', ''],
    viernes:   ['09:00', '17:00', '', ''],
    sábado:    ['10:00', '14:00', '', ''],
  });
  // Turno partido L-V con tarde corta los viernes.
  const t2Sched = scheduleCells({
    lunes:     ['09:00', '13:00', '16:00', '20:00'],
    martes:    ['09:00', '13:00', '16:00', '20:00'],
    miércoles: ['09:00', '13:00', '16:00', '20:00'],
    jueves:    ['09:00', '13:00', '16:00', '20:00'],
    viernes:   ['09:00', '13:00', '16:00', '18:00'],
  });
  // Hostelería: solo tarde mar-dom (lunes descansa).
  const t3Sched = scheduleCells({
    martes:    ['', '', '16:00', '22:00'],
    miércoles: ['', '', '16:00', '22:00'],
    jueves:    ['', '', '16:00', '22:00'],
    viernes:   ['', '', '16:00', '23:00'],
    sábado:    ['', '', '16:00', '23:00'],
    domingo:   ['', '', '13:00', '20:00'],
  });

  // Contraseñas de ejemplo que CUMPLEN la política real (antes eran
  // "worker123"… y la propia plantilla no se podía importar).
  const sample = [
    ['Pablo Serrano', '12345678G', '410012345678', 'pablo@example.com', '+34600000001', SAMPLE_PASSWORDS[0],
      'Trabajador', sedeA,
      'SÍ', 'NO', 'Validación de jornada', 'SÍ',
      'Sevilla', 'Sevilla', 'Andalucía', exampleAgreement,
      ...t1Sched],
    ['Elena Torres', '12345678A', '', 'elena@example.com', '+34600000002', SAMPLE_PASSWORDS[1],
      'Admin empresa', '',
      'NO', 'NO', 'Fichaje libre', 'SÍ',
      'Sevilla', 'Sevilla', 'Andalucía', '',
      ...t2Sched],
    ['Laura Gomez', '12345678C', '', 'laura@example.com', '+34600000003', SAMPLE_PASSWORDS[2],
      'Admin sede', sedeB,
      'SÍ', 'NO', 'Fichaje mixto', 'SÍ',
      'Sevilla', 'Sevilla', 'Andalucía', '',
      ...t3Sched],
  ];
  const ws = XLSX.utils.aoa_to_sheet([WORKER_HEADERS_ES, ...sample]);
  const scheduleStartCol = WORKER_HEADERS_ES.length - SCHEDULE_HEADERS_ES.length;
  ws['!cols'] = WORKER_HEADERS_ES.map((h, idx) => (
    idx >= scheduleStartCol ? { wch: 12 } : { wch: Math.max(12, Math.min(28, h.length + 2)) }
  ));

  // Formato "h:mm" en las 28 columnas de horario; sin esto Excel mostraría
  // "0,375" en lugar de "09:00".
  for (let r = 1; r <= sample.length; r++) {
    for (let c = scheduleStartCol; c < WORKER_HEADERS_ES.length; c++) {
      const cell = ws[XLSX.utils.encode_cell({ r, c })];
      if (cell && typeof cell.v === 'number') {
        cell.t = 'n';
        cell.z = 'h:mm';
      } else if (cell && (cell.v === '' || cell.v == null)) {
        cell.z = 'h:mm';
      }
    }
  }
  XLSX.utils.book_append_sheet(wb, ws, 'Trabajadores');

  const tipos = Object.values(USER_TYPE_LABELS).join(' · ');
  const conveniosDisp = agreements.length
    ? agreements.map((a) => `  · ${a.name}`)
    : ['  (todavía no hay convenios creados — déjalo en blanco o créalos primero en /panel/convenios)'];
  const sedesDisp = wpNames.length
    ? wpNames.map((n) => `  · ${n}`)
    : ['  (la empresa todavía no tiene sedes — créalas antes o importa primero la plantilla de sedes)'];

  const instr = [
    [`Plantilla de TRABAJADORES — ${companyName}`],
    [],
    ['Cabeceras (no las renombres ni añadas nada a la celda: la fila 1 debe contener SOLO el nombre de la columna)'],
    ...WORKER_HEADERS_ES.map((h) => [`  · ${h}`]),
    [],
    ['Formato del archivo'],
    [`  · ${FILE_FORMATS_TEXT}`],
    ['  · En CSV/TSV escribe las horas como texto (9:30) y los NIF/teléfonos tal cual: se respetan los ceros a la izquierda.'],
    [],
    ['Cómo rellenar cada columna'],
    ['  · Obligatorias: Nombre completo, Email, Contraseña y Tipo de fichaje. NIF y Teléfono también son necesarios para el alta.'],
    [`  · Contraseña (obligatoria): ${PASSWORD_POLICY_TEXT}. Ej.: ${SAMPLE_PASSWORDS[0]}`],
    [`  · Tipo de usuario: uno de → ${tipos}`],
    [`  · Sede: nombre EXACTO de una sede existente de "${companyName}" (NO el nombre de la empresa). Vacío = sin sede.`],
    ['  · Si una sede no existe, al importar podrás asignarla a una sede existente o dejar al trabajador sin sede.'],
    ['  · Los trabajadores cuyo email o NIF ya existan en la empresa se omiten (no se duplican).'],
    ['  · Booleanos (Geolocalización / Remoto / Login habilitado): SÍ o NO.'],
    ['  · Tipo de fichaje (OBLIGATORIO): elige UNA y solo UNA opción:'],
    ['      · "Validación de jornada" → el trabajador SOLO puede firmar dentro de su turno. No permite fichar en diferido ni horas extra.'],
    ['      · "Fichaje libre" → puede fichar en diferido y todo el tiempo trabajado fuera del turno cuenta automáticamente como horas extra.'],
    ['      · "Fichaje mixto" → no puede fichar antes de la hora de entrada, pero al final del turno SÍ cuentan las horas extra.'],
    ['  · Provincia, Localidad y Comunidad autónoma: nombres como en España (ej. Sevilla, Andalucía).'],
    ['  · Convenio: nombre exacto de un convenio existente (o vacío para no asignar).'],
    [],
    ['Jornada laboral — 28 columnas (4 por día × 7 días)'],
    ['  · Cada día tiene cuatro columnas: entrada mañana, salida mañana, entrada tarde, salida tarde.'],
    ['  · Las 28 columnas vienen pre-formateadas como "Hora" en Excel — cuando escribas 9:00 Excel lo interpretará como hora automáticamente. NO cambies el formato a Texto.'],
    ['  · Formato 24h. Ejemplos válidos: 09:00, 17:30, 23:45.'],
    ['  · Si el trabajador SOLO trabaja por la mañana, deja las dos columnas de tarde en blanco.'],
    ['  · Si SOLO trabaja por la tarde, deja las dos columnas de mañana en blanco.'],
    ['  · Si trabaja en turno partido, rellena las cuatro columnas del día.'],
    ['  · Si NO trabaja ese día (descanso), deja las cuatro columnas en blanco.'],
    ['  · Si rellenas alguna hora de un día, ese día se marca automáticamente como que sí trabaja.'],
    [],
    ['Sedes disponibles ahora mismo'],
    ...sedesDisp.map((l) => [l]),
    [],
    ['Convenios disponibles ahora mismo'],
    ...conveniosDisp.map((l) => [l]),
  ];
  const wsI = XLSX.utils.aoa_to_sheet(instr);
  wsI['!cols'] = [{ wch: 100 }];
  XLSX.utils.book_append_sheet(wb, wsI, 'Instrucciones');

  const safe = (companyName || 'empresa').replace(/[^A-Za-z0-9_-]+/g, '_');
  XLSX.writeFile(wb, `plantilla_trabajadores_${safe}.xlsx`);
};

// ─────────────────────────────────────────────────────────────
// Lectura de archivos
// ─────────────────────────────────────────────────────────────
const describeFile = (fileName, parsed) => {
  if (parsed.format === 'xlsx') {
    return `${fileName} · Excel${parsed.sheetName ? ` · hoja «${parsed.sheetName}»` : ''}`;
  }
  const enc = { 'utf-8': 'UTF-8', 'utf-8-bom': 'UTF-8 (BOM)', 'windows-1252': 'Windows-1252', 'utf-16le': 'UTF-16', 'utf-16be': 'UTF-16' }[parsed.encoding] || parsed.encoding;
  return `${fileName} · texto ${enc} · separador ${delimiterLabel(parsed.delimiter)}`;
};

const loadSedesFile = async (file) => {
  const parsed = await readSpreadsheetFile(file, { sheetCandidates: ['Sedes'] });
  const { sedes, dropped } = rowsToSedes(parsed.rows);
  return {
    rows: sedes, dropped,
    diag: diagnoseHeaders(parsed.headers, 'sedes'),
    fileInfo: describeFile(file.name, parsed),
    headerCount: parsed.headers.length,
  };
};

const loadWorkersFile = async (file) => {
  const parsed = await readSpreadsheetFile(file, { sheetCandidates: ['Trabajadores'] });
  const { workers, dropped } = rowsToWorkers(parsed.rows);
  return {
    rows: workers, dropped,
    diag: diagnoseHeaders(parsed.headers, 'workers'),
    fileInfo: describeFile(file.name, parsed),
    headerCount: parsed.headers.length,
  };
};

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;
const scrollToIssue = (id) => {
  const el = typeof document !== 'undefined' ? document.getElementById(id) : null;
  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
};

// ─────────────────────────────────────────────────────────────
// Modal
// ─────────────────────────────────────────────────────────────
const BulkImportModal = ({ open, company, onClose, onDone }) => {
  const sedesInputRef = useRef(null);
  const workersInputRef = useRef(null);
  const prevOpenRef = useRef(false);
  const importedRef = useRef(false);

  // { rows, dropped, diag, fileInfo } | null
  const [sedesData, setSedesData] = useState(null);
  const [sedesError, setSedesError] = useState(null);
  const [workersData, setWorkersData] = useState(null);
  const [workersError, setWorkersError] = useState(null);

  const [agreements, setAgreements] = useState([]);
  // Datos de referencia de la empresa, cargados al abrir.
  const [refData, setRefData] = useState({ loading: false, workplaces: [], workers: [], wpError: null, workersError: null });
  const [refReloadTick, setRefReloadTick] = useState(0);
  // Asignación de sedes desconocidas: { [clave]: 'none' | 'wp:<id>' | 'file:<clave>' }
  const [sedeMapping, setSedeMapping] = useState({});

  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [report, setReport] = useState(null);
  const toast = useToast();

  const companyId = company?.id;

  // Reset SOLO en la transición cerrado → abierto. Antes se reseteaba
  // también cuando la página recargaba la empresa, y el informe final
  // desaparecía sin dejar rastro.
  useEffect(() => {
    const wasOpen = prevOpenRef.current;
    prevOpenRef.current = open;
    if (!open || wasOpen) return;
    importedRef.current = false;
    setSedesData(null); setWorkersData(null);
    setSedesError(null); setWorkersError(null);
    setSedeMapping({});
    setReport(null); setProgress({ done: 0, total: 0 });
    setRunning(false);
  }, [open]);

  // Convenios de la empresa destino + globales. El superadmin recibe los de
  // todas las empresas (y el backend antiguo también): filtramos en cliente.
  useEffect(() => {
    if (!open || companyId == null) return;
    listAgreements(companyId)
      .then((list) => setAgreements(agreementsForCompany(Array.isArray(list) ? list : [], companyId)))
      .catch(() => setAgreements([]));
  }, [open, companyId]);

  // Sedes y trabajadores existentes: para validar sedes y duplicados ANTES
  // de importar.
  useEffect(() => {
    if (!open || companyId == null) return undefined;
    let cancelled = false;
    setRefData((prev) => ({ ...prev, loading: true, wpError: null, workersError: null }));
    Promise.allSettled([getWorkplaces(companyId), getCompanyWorkers(companyId)]).then(([wp, ws]) => {
      if (cancelled) return;
      setRefData({
        loading: false,
        workplaces: wp.status === 'fulfilled' && Array.isArray(wp.value) ? wp.value : [],
        workers: ws.status === 'fulfilled' && Array.isArray(ws.value) ? ws.value : [],
        wpError: wp.status === 'rejected' ? (wp.reason?.message || 'Error desconocido') : null,
        workersError: ws.status === 'rejected' ? (ws.reason?.message || 'Error desconocido') : null,
      });
    });
    return () => { cancelled = true; };
  }, [open, companyId, refReloadTick]);

  const sedes = sedesData?.rows || null;
  const workers = workersData?.rows || null;

  const unknownSedes = useMemo(
    () => (refData.loading || refData.wpError ? [] : findUnknownSedes(workers, refData.workplaces, sedes)),
    [workers, sedes, refData],
  );
  const pendingSedes = unresolvedSedes(unknownSedes, sedeMapping);
  const dupes = useMemo(
    () => (refData.loading || refData.workersError ? { existing: {}, inFile: [] } : findDuplicates(workers, refData.workers)),
    [workers, refData],
  );

  if (!open || !company) return null;

  const handleSedesFile = async (f) => {
    setSedesError(null); setSedesData(null);
    try {
      const data = await loadSedesFile(f);
      if (data.rows.length === 0 && !data.diag.blocking) {
        setSedesError(data.headerCount === 0
          ? 'El archivo está vacío o no tiene fila de cabeceras.'
          : 'El archivo no tiene filas válidas de sedes.');
        if (data.dropped.length === 0) return;
      }
      setSedesData(data);
    } catch (err) {
      setSedesError(err.message || 'No se pudo leer el archivo de sedes.');
    }
  };

  const handleWorkersFile = async (f) => {
    setWorkersError(null); setWorkersData(null);
    try {
      const data = await loadWorkersFile(f);
      if (data.rows.length === 0 && !data.diag.blocking) {
        setWorkersError(data.headerCount === 0
          ? 'El archivo está vacío o no tiene fila de cabeceras.'
          : 'El archivo no tiene filas válidas de trabajadores.');
        if (data.dropped.length === 0) return;
      }
      setWorkersData(data);
    } catch (err) {
      setWorkersError(err.message || 'No se pudo leer el archivo de trabajadores.');
    }
  };

  const handleClose = () => {
    if (running) return;
    onClose();
    // La recarga de la página se lanza al CERRAR, no al terminar: así nada
    // puede desmontar el modal mientras el usuario lee el informe.
    if (importedRef.current && typeof onDone === 'function') onDone();
    importedRef.current = false;
  };

  const runImport = async () => {
    setRunning(true);
    importedRef.current = true;
    const total = (sedes?.length || 0) + (workers?.length || 0);
    setProgress({ done: 0, total });
    const log = { sedes: [], trabajadores: [], errors: [] };
    let done = 0;
    const tick = () => { done++; setProgress({ done, total }); };

    try {
      // Sedes existentes (recién leídas; si falla, las cargadas al abrir).
      let wpList = refData.workplaces;
      try {
        const fresh = await getWorkplaces(company.id);
        if (Array.isArray(fresh)) wpList = fresh;
      } catch (err) {
        log.errors.push(`No se pudieron recargar las sedes de la empresa (${err.message}). Se usa la lista cargada al abrir.`);
      }
      const wpIndex = indexWorkplaces(wpList);

      // Trabajadores existentes para no crear duplicados.
      let existingWorkers = refData.workers;
      try {
        const fresh = await getCompanyWorkers(company.id);
        if (Array.isArray(fresh)) existingWorkers = fresh;
      } catch (err) {
        log.errors.push(`No se pudieron recargar los trabajadores de la empresa (${err.message}). Se usa la lista cargada al abrir.`);
      }

      // 1) Sedes
      for (const s of sedes || []) {
        try {
          const key = sedeKey(s.name);
          if (wpIndex.has(key)) {
            log.sedes.push({ row: s._row, name: s.name, ok: true, skipped: 'ya existía' });
          } else {
            const created = await createWorkplace(company.id, {
              name: s.name, email: s.email, phone: s.phone, isLoginEnabled: s.isLoginEnabled,
            });
            wpIndex.set(key, created);
            log.sedes.push({ row: s._row, name: s.name, ok: true });
          }
        } catch (err) {
          log.sedes.push({ row: s._row, name: s.name, ok: false, err: err.message });
        }
        tick();
      }

      // Si alguna sede creada no devolvió id, recargamos para poder asignarla.
      if ((sedes || []).length && [...wpIndex.values()].some((w) => w?.id == null)) {
        try {
          const fresh = await getWorkplaces(company.id);
          indexWorkplaces(fresh).forEach((w, k) => wpIndex.set(k, w));
        } catch (err) {
          log.errors.push(`No se pudieron recargar las sedes recién creadas (${err.message}).`);
        }
      }

      // 2) Trabajadores
      // `agreements` ya viene con los de la empresa primero y los globales
      // después: ante nombres repetidos gana el de la empresa.
      const agreementByName = new Map();
      for (const a of agreements) {
        const key = norm(a.name);
        if (!agreementByName.has(key)) agreementByName.set(key, a);
      }

      for (const t of workers || []) {
        const dup = existingMatch(t, existingWorkers);
        if (dup) {
          log.trabajadores.push({
            row: t._row, name: t.name, ok: true,
            skipped: `ya existe (${dup.field === 'email' ? 'email' : 'NIF'} ${dup.value}) — omitido`,
          });
          tick();
          continue;
        }
        // Red de seguridad: la UI ya obliga a asignar las sedes desconocidas.
        const sede = resolveWorkerSede(t.workplaceName, wpIndex, sedeMapping);
        if (sede.status === 'missing') {
          log.trabajadores.push({ row: t._row, name: t.name, ok: false, err: `Sede "${t.workplaceName}" no encontrada` });
          tick();
          continue;
        }
        const workingSite = sede.status === 'ok' ? sede.id : null;
        let agreement = null;
        if (t.agreementName) {
          const a = agreementByName.get(norm(t.agreementName));
          if (a) agreement = a.id;
          else {
            log.trabajadores.push({ row: t._row, name: t.name, ok: false, err: `Convenio "${t.agreementName}" no encontrado` });
            tick();
            continue;
          }
        }
        try {
          const payload = {
            companyId: company.id,
            type: t.type,
            name: t.name,
            nif: t.nif,
            ssNumber: t.ssNumber,
            email: t.email,
            phone: t.phone,
            password: t.password || undefined,
            geoTracking: t.geoTracking,
            worksRemotely: t.worksRemotely,
            canRequestCheckins: t.canRequestCheckins,
            overtimeAllowed: t.overtimeAllowed,
            enforceStartHour: t.enforceStartHour,
            isLoginEnabled: t.isLoginEnabled,
            workingSite,
            province: t.province,
            locality: t.locality,
            autonomousCommunity: t.autonomousCommunity,
            agreement,
          };
          const created = await addWorker(payload);
          // El backend NO devuelve el id en el POST de creación. Lookup por
          // email (case-insensitive) y, si no, NIF: primero en la empresa y
          // después globalmente como fallback.
          let newId = created?.id || created?.data?.id || null;

          const lookupId = (arr) => {
            const list = Array.isArray(arr) ? arr : [];
            const byEmail = t.email
              ? list.find((w) => (w.email || '').toLowerCase().trim() === t.email.toLowerCase().trim())
              : null;
            if (byEmail?.id) return byEmail.id;
            const byNif = t.nif
              ? list.find((w) => (w.nif || '').toUpperCase().trim() === t.nif.toUpperCase().trim())
              : null;
            return byNif?.id || null;
          };

          if (!newId) {
            try {
              newId = lookupId(await getCompanyWorkers(company.id));
            } catch (err) {
              // eslint-disable-next-line no-console
              console.warn('[bulk-import] getCompanyWorkers fallo:', err);
            }
          }
          if (!newId) {
            try {
              newId = lookupId(await searchWorkers(''));
            } catch (err) {
              // eslint-disable-next-line no-console
              console.warn('[bulk-import] searchWorkers fallo:', err);
            }
          }

          if (newId && Array.isArray(t.week)) {
            try {
              await saveWeekSchedule(newId, t.week.map(toBackendDay));
              log.trabajadores.push({ row: t._row, name: t.name, ok: true });
            } catch (err) {
              log.trabajadores.push({ row: t._row, name: t.name, ok: true, warn: `creado pero la jornada falló: ${err.message}` });
            }
          } else if (newId) {
            log.trabajadores.push({ row: t._row, name: t.name, ok: true, warn: 'creado pero la jornada estaba vacía o no parseable.' });
          } else {
            // eslint-disable-next-line no-console
            console.error(`[bulk-import] NO se encontro el id del worker recien creado. email='${t.email}', nif='${t.nif}'. La jornada NO se guarda.`);
            log.trabajadores.push({ row: t._row, name: t.name, ok: true, warn: 'creado pero no se pudo recuperar el id del trabajador para guardar la jornada.' });
          }
          // Evita duplicar si el mismo email/NIF apareciera otra vez.
          existingWorkers = [...existingWorkers, { email: t.email, nif: t.nif }];
        } catch (err) {
          log.trabajadores.push({ row: t._row, name: t.name, ok: false, err: err.message });
        }
        tick();
      }
    } catch (err) {
      // Cualquier fallo inesperado termina en un informe visible.
      // eslint-disable-next-line no-console
      console.error('[bulk-import] error inesperado:', err);
      log.errors.push(`Error inesperado, la importación se detuvo: ${err?.message || String(err)}`);
    } finally {
      setReport(log);
      setRunning(false);
      // El informe en pantalla es el detalle; esto es solo el resumen final.
      const all = [...log.sedes, ...log.trabajadores];
      const summary = FEEDBACK.workersImported({
        created: all.filter((e) => e.ok && !e.skipped).length,
        skipped: all.filter((e) => e.ok && e.skipped).length,
        failed: all.filter((e) => !e.ok).length,
        incidents: log.errors.length,
      });
      toast.show(summary.type, summary.message);
    }
  };

  // ── Motivos de bloqueo ──
  const sedesDiag = sedesData?.diag;
  const workersDiag = workersData?.diag;
  const workerPwdIssues = (workers || []).filter((w) => w._pwdIssue && !dupes.existing[w._row]);
  const workerFichajeIssues = (workers || []).filter((w) => w._fichajeIssue && !dupes.existing[w._row]);
  const totalRows = (sedes?.length || 0) + (workers?.length || 0);
  // Sin la lista de sedes no sabemos cuáles ya existen (crearíamos
  // duplicadas); sin la de trabajadores no podemos evitar duplicados.
  const refBlocked = totalRows > 0 && (
    refData.loading
    || !!refData.wpError
    || ((workers?.length || 0) > 0 && !!refData.workersError)
  );

  const blockers = [];
  if (sedesDiag?.wrongFile) blockers.push({ id: 'bulk-issue-sedes-cols', text: 'archivo de sedes incorrecto' });
  (sedesDiag?.missing || []).forEach((m) => blockers.push({ id: 'bulk-issue-sedes-cols', text: `falta columna «${m.label}» (sedes)` }));
  (workersDiag?.missing || []).forEach((m) => blockers.push({ id: 'bulk-issue-workers-cols', text: `falta columna «${m.label}»` }));
  if (workerPwdIssues.length) blockers.push({ id: 'bulk-issue-pwd', text: `${plural(workerPwdIssues.length, 'fila', 'filas')} con contraseña inválida` });
  if (workerFichajeIssues.length) blockers.push({ id: 'bulk-issue-fichaje', text: `${plural(workerFichajeIssues.length, 'fila', 'filas')} sin tipo de fichaje válido` });
  if (dupes.inFile.length) blockers.push({ id: 'bulk-issue-dupes', text: `${plural(dupes.inFile.length, 'email/NIF repetido', 'emails/NIF repetidos')} en el archivo` });
  if (pendingSedes.length) blockers.push({ id: 'bulk-issue-sedes-map', text: `${plural(pendingSedes.length, 'sede sin asignar', 'sedes sin asignar')}` });
  if (refBlocked) {
    blockers.push({
      id: 'bulk-issue-ref',
      text: refData.loading ? 'cargando sedes y trabajadores de la empresa…' : 'no se pudieron cargar sedes/trabajadores de la empresa',
    });
  }

  const existingCount = Object.keys(dupes.existing).length;
  const blockedText = blockers.length ? `No se puede importar: ${blockers.map((b) => b.text).join(' · ')}` : '';
  const canImport = totalRows > 0 && !running && blockers.length === 0;
  const buttonTitle = running
    ? 'Importación en curso'
    : (blockedText || (totalRows === 0 ? 'Sube al menos un archivo con filas válidas' : 'Iniciar importación'));

  const wpOptions = refData.workplaces.filter((w) => w?.id != null && w?.name);
  const fileSedeOptions = (sedes || []).filter((s) => !indexWorkplaces(refData.workplaces).has(sedeKey(s.name)));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 backdrop-blur-sm p-4 overflow-y-auto">
      <div className="bg-white w-full max-w-3xl rounded-2xl shadow-2xl my-8 max-h-[92vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 shrink-0">
          <div>
            <div className="text-xs uppercase tracking-wider text-ink-muted font-semibold">Importación masiva</div>
            <h2 className="font-display text-lg font-bold text-ink">{company.name}</h2>
          </div>
          <button onClick={handleClose} disabled={running} className="text-ink-muted hover:text-ink p-1 rounded-lg hover:bg-slate-100 transition-colors disabled:opacity-40 disabled:cursor-not-allowed" aria-label="Cerrar">
            <X size={18} />
          </button>
        </div>

        <div className="px-6 py-5 overflow-y-auto flex-1 space-y-5">
          {!report && (
            <p className="text-sm text-ink-muted">
              Sube los archivos por separado (Excel, CSV o TSV). Si subes ambos, se importarán primero las sedes y después los trabajadores
              (los trabajadores podrán referirse a las sedes recién creadas por su nombre).
            </p>
          )}

          {!report && (
            <ImportSection
              icon={MapPin}
              title="Sedes"
              description="Crea sedes nuevas para esta empresa. Las que ya existan con el mismo nombre se omitirán."
              templateLabel="Descargar plantilla de sedes"
              onTemplate={() => downloadSedesTemplate(company.name)}
              onPick={(f) => handleSedesFile(f)}
              fileInputRef={sedesInputRef}
              parsed={sedes}
              fileInfo={sedesData?.fileInfo}
              error={sedesError}
              previewKeys={['name']}
              previewLabel="Sedes detectadas"
            />
          )}

          {!report && sedesData && (
            <FileDiagnostics id="bulk-issue-sedes-cols" diag={sedesDiag} dropped={sedesData.dropped} kindLabel="sedes" />
          )}

          {!report && (
            <ImportSection
              icon={Users}
              title="Trabajadores"
              description="Crea trabajadores con su jornada laboral semanal. La columna Sede debe coincidir con una sede existente o de la importada arriba. Cada día tiene 4 columnas (mañana × tarde × entrada/salida); deja en blanco las que no apliquen."
              templateLabel="Descargar plantilla de trabajadores"
              onTemplate={() => downloadWorkersTemplate(company.name, agreements, refData.workplaces)}
              onPick={(f) => handleWorkersFile(f)}
              fileInputRef={workersInputRef}
              parsed={workers}
              fileInfo={workersData?.fileInfo}
              error={workersError}
              previewKeys={['name', 'workplaceName']}
              previewLabel="Trabajadores detectados"
              rowBadge={(r) => (dupes.existing[r._row] ? 'Ya existe — se omitirá' : null)}
            />
          )}

          {!report && workersData && (
            <FileDiagnostics id="bulk-issue-workers-cols" diag={workersDiag} dropped={workersData.dropped} kindLabel="trabajadores" />
          )}

          {!report && refBlocked && (
            <div id="bulk-issue-ref" className={`rounded-xl p-4 border ${refData.loading ? 'border-slate-200 bg-slate-50' : 'border-danger/30 bg-danger/5'}`}>
              {refData.loading ? (
                <div className="flex items-center gap-2 text-sm text-ink-muted">
                  <Loader2 size={14} className="animate-spin" /> Cargando sedes y trabajadores de la empresa para validar el archivo…
                </div>
              ) : (
                <div className="flex items-start gap-2">
                  <AlertTriangle size={16} className="text-danger shrink-0 mt-0.5" />
                  <div className="text-sm">
                    <div className="font-bold text-danger">No se pudieron cargar los datos de la empresa</div>
                    {refData.wpError && <div className="text-xs text-ink-muted">Sedes: {refData.wpError}</div>}
                    {refData.workersError && <div className="text-xs text-ink-muted">Trabajadores: {refData.workersError}</div>}
                    <div className="text-xs text-ink-muted mt-0.5">Sin ellos no podemos comprobar sedes ni duplicados.</div>
                    <button type="button" onClick={() => setRefReloadTick((n) => n + 1)} className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-danger hover:underline">
                      <RefreshCw size={11} /> Reintentar
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {!report && unknownSedes.length > 0 && (
            <div id="bulk-issue-sedes-map" className={`rounded-xl p-4 border ${pendingSedes.length ? 'border-danger/30 bg-danger/5' : 'border-success/30 bg-success/5'}`}>
              <div className="flex items-start gap-2 mb-2">
                {pendingSedes.length
                  ? <AlertTriangle size={16} className="text-danger shrink-0 mt-0.5" />
                  : <CheckCircle2 size={16} className="text-success shrink-0 mt-0.5" />}
                <div>
                  <div className={`text-sm font-bold ${pendingSedes.length ? 'text-danger' : 'text-success'}`}>
                    {plural(unknownSedes.length, 'sede del archivo no existe', 'sedes del archivo no existen')} en {company.name}
                  </div>
                  <div className="text-[11px] text-ink-muted mt-0.5">
                    La columna «Sede» debe ser el nombre de una sede de la empresa (no el de la empresa).
                    Sedes disponibles: {wpOptions.length ? wpOptions.map((w) => `«${w.name}»`).join(', ') : 'ninguna'}
                    {fileSedeOptions.length > 0 && <> · del archivo de sedes: {fileSedeOptions.map((s) => `«${s.name}»`).join(', ')}</>}.
                    Elige a qué sede asignar cada una:
                  </div>
                </div>
              </div>
              <ul className="space-y-2 pl-6">
                {unknownSedes.map((u) => (
                  <li key={u.key} className="flex items-center gap-2 flex-wrap text-xs">
                    <span className="font-semibold text-ink">«{u.name}»</span>
                    <span className="text-ink-muted">({plural(u.rows.length, 'fila', 'filas')}: {u.rows.slice(0, 6).map((r) => `#${r}`).join(', ')}{u.rows.length > 6 ? '…' : ''})</span>
                    <span className="text-ink-muted">→ Asignar a:</span>
                    <select
                      value={sedeMapping[u.key] || ''}
                      onChange={(e) => setSedeMapping((prev) => ({ ...prev, [u.key]: e.target.value }))}
                      className={`border rounded-lg px-2 py-1 text-xs bg-white ${sedeMapping[u.key] ? 'border-slate-300' : 'border-danger'}`}
                    >
                      <option value="">— Elige una opción —</option>
                      {wpOptions.map((w) => (
                        <option key={`wp-${w.id}`} value={`wp:${w.id}`}>{w.name}</option>
                      ))}
                      {fileSedeOptions.map((s) => (
                        <option key={`file-${sedeKey(s.name)}`} value={`file:${sedeKey(s.name)}`}>{s.name} (archivo de sedes)</option>
                      ))}
                      <option value="none">Sin sede</option>
                    </select>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {!report && existingCount > 0 && (
            <div className="border border-warning/30 bg-warning/5 rounded-xl p-4">
              <div className="flex items-start gap-2">
                <Info size={16} className="text-warning shrink-0 mt-0.5" />
                <div>
                  <div className="text-sm font-bold text-ink">
                    {plural(existingCount, 'trabajador ya existe', 'trabajadores ya existen')} en la empresa — se omitirá{existingCount === 1 ? '' : 'n'}
                  </div>
                  <ul className="text-xs space-y-0.5 mt-1 max-h-32 overflow-y-auto">
                    {(workers || []).filter((w) => dupes.existing[w._row]).map((w) => (
                      <li key={w._row} className="text-ink-muted">
                        <span className="text-ink-muted/60 tabular-nums">#{w._row}</span>{' '}
                        <span className="text-ink font-medium">{w.name}</span>
                        {' · '}mismo {dupes.existing[w._row].field === 'email' ? 'email' : 'NIF'} ({dupes.existing[w._row].value})
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}

          {!report && dupes.inFile.length > 0 && (
            <IssueList
              id="bulk-issue-dupes"
              title={`${plural(dupes.inFile.length, 'valor repetido', 'valores repetidos')} dentro del archivo`}
              hint="Cada trabajador debe tener un email y un NIF únicos. Corrige el archivo y vuelve a subirlo."
              items={dupes.inFile.map((d) => ({
                key: `${d.field}-${d.value}`,
                label: `${d.field === 'email' ? 'Email' : 'NIF'} ${d.value}`,
                detail: `filas ${d.rows.map((r) => `#${r}`).join(', ')}`,
              }))}
            />
          )}

          {!report && workerPwdIssues.length > 0 && (
            <IssueList
              id="bulk-issue-pwd"
              title={`${workerPwdIssues.length} contraseña${workerPwdIssues.length === 1 ? '' : 's'} no cumple${workerPwdIssues.length === 1 ? '' : 'n'} la política de seguridad`}
              hint={`Mínimo ${PASSWORD_MIN_LENGTH} caracteres, con mayúscula, minúscula, número y símbolo. Corrige el archivo y vuelve a subirlo — no podemos iniciar la importación hasta que todas sean válidas.`}
              items={workerPwdIssues.map((w) => ({ key: w._row, row: w._row, label: w.name, detail: w._pwdIssue }))}
            />
          )}

          {!report && workerFichajeIssues.length > 0 && (
            <IssueList
              id="bulk-issue-fichaje"
              title={`${workerFichajeIssues.length} trabajador${workerFichajeIssues.length === 1 ? '' : 'es'} sin "Tipo de fichaje" válido`}
              hint="El campo es obligatorio y debe ser uno de: Validación de jornada, Fichaje libre o Fichaje mixto. Corrige el archivo y vuelve a subirlo."
              items={workerFichajeIssues.map((w) => ({ key: w._row, row: w._row, label: w.name, detail: w._fichajeIssue }))}
            />
          )}

          {running && (
            <div>
              <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                <div className="h-full bg-primary transition-all" style={{ width: `${progress.total ? (progress.done / progress.total) * 100 : 0}%` }} />
              </div>
              <div className="mt-1 text-xs text-ink-muted text-right tabular-nums">{progress.done} / {progress.total}</div>
            </div>
          )}

          {report && (
            <div>
              {report.errors.length > 0 && (
                <div className="border border-danger/30 bg-danger/5 rounded-xl p-4 mb-3">
                  <div className="flex items-center gap-2 mb-1">
                    <AlertTriangle size={16} className="text-danger" />
                    <span className="text-sm font-bold text-danger">Incidencias de la importación</span>
                  </div>
                  <ul className="text-xs space-y-1 pl-6 list-disc text-ink">
                    {report.errors.map((e, i) => <li key={i}>{e}</li>)}
                  </ul>
                </div>
              )}
              <ResultBlock title="Sedes" entries={report.sedes} icon={MapPin} />
              <ResultBlock title="Trabajadores" entries={report.trabajadores} icon={Users} />
              {report.sedes.length + report.trabajadores.length === 0 && report.errors.length === 0 && (
                <div className="text-sm text-ink-muted">No se procesó ninguna fila.</div>
              )}
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-between gap-3 shrink-0">
          <div className="text-xs text-ink-muted min-w-0">
            {!report && blockers.length > 0 && (totalRows > 0 || sedesData || workersData) && (
              <span className="text-danger">
                {blockedText}.{' '}
                <button type="button" onClick={() => scrollToIssue(blockers[0].id)} className="font-semibold underline hover:no-underline">
                  Ver detalles
                </button>
              </span>
            )}
            {!report && blockers.length === 0 && totalRows > 0 && (
              `Listos: ${sedes?.length || 0} sede(s) · ${workers?.length || 0} trabajador(es)${existingCount ? ` (${existingCount} ya existen y se omitirán)` : ''}.`
            )}
            {report && (() => {
              const all = [...report.sedes, ...report.trabajadores];
              const skipped = all.filter((e) => e.ok && e.skipped).length;
              const ok = all.filter((e) => e.ok && !e.skipped).length;
              const failed = all.filter((e) => !e.ok).length;
              return `Importación finalizada: ${ok} creados · ${skipped} omitidos · ${failed} con error${report.errors.length ? ` · ${plural(report.errors.length, 'incidencia', 'incidencias')}` : ''}.`;
            })()}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {!report && (
              <button
                type="button"
                onClick={handleClose}
                disabled={running}
                className="px-3 py-2 rounded-lg text-sm font-semibold text-ink-muted hover:bg-slate-100 transition-colors disabled:cursor-not-allowed"
              >
                Cancelar
              </button>
            )}
            {!report && (
              <button
                type="button"
                onClick={runImport}
                disabled={!canImport}
                title={buttonTitle}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold bg-primary text-white hover:bg-primary-dark transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {running ? <Loader2 size={15} className="animate-spin" /> : <Play size={15} />}
                {running ? 'Importando…' : 'Iniciar importación'}
              </button>
            )}
            {report && (
              <button type="button" onClick={handleClose} className="px-4 py-2 rounded-lg text-sm font-semibold bg-ink text-white hover:bg-slate-800 transition-colors">
                Cerrar
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Columnas no reconocidas / obligatorias que faltan / filas descartadas.
const FileDiagnostics = ({ id, diag, dropped, kindLabel }) => {
  if (!diag) return null;
  const hasBlocking = diag.blocking;
  const hasInfo = diag.unknown.length > 0 || dropped.length > 0;
  if (!hasBlocking && !hasInfo) return null;
  return (
    <div id={id} className="space-y-2 -mt-3">
      {diag.wrongFile && (
        <div className="border border-danger/30 bg-danger/5 rounded-xl px-4 py-3 text-sm font-bold text-danger flex items-start gap-2">
          <AlertTriangle size={16} className="shrink-0 mt-0.5" /> {diag.wrongFile}
        </div>
      )}
      {diag.missing.map((m) => (
        <div key={m.field} className="border border-danger/30 bg-danger/5 rounded-xl px-4 py-3 flex items-start gap-2">
          <AlertTriangle size={16} className="text-danger shrink-0 mt-0.5" />
          <div className="text-sm">
            <span className="font-bold text-danger">Falta la columna obligatoria «{m.label}»</span>
            {m.suggestion && <span className="text-danger"> (¿quizá «{m.suggestion}»?)</span>}
            <div className="text-[11px] text-ink-muted mt-0.5">
              La fila 1 debe contener exactamente «{m.label}». Corrige la cabecera y vuelve a subir el archivo.
            </div>
          </div>
        </div>
      ))}
      {diag.unknown.length > 0 && (
        <div className="border border-warning/30 bg-warning/5 rounded-xl px-4 py-3 text-xs text-ink flex items-start gap-2">
          <Info size={14} className="text-warning shrink-0 mt-0.5" />
          <div>
            <span className="font-semibold">Columnas no reconocidas (se ignorarán): </span>
            {diag.unknown.map((u, i) => (
              <span key={`${u.header}-${i}`}>
                {i > 0 && ', '}«{u.header}»{u.suggestion && <span className="text-ink-muted"> (¿quizá «{u.suggestion}»?)</span>}
              </span>
            ))}
          </div>
        </div>
      )}
      {dropped.length > 0 && (
        <div className="border border-warning/30 bg-warning/5 rounded-xl px-4 py-3 text-xs text-ink flex items-start gap-2">
          <Info size={14} className="text-warning shrink-0 mt-0.5" />
          <div>
            <span className="font-semibold">{plural(dropped.length, 'fila descartada', 'filas descartadas')} de {kindLabel}:</span>{' '}
            {dropped.slice(0, 8).map((d) => `#${d.row} (${d.reason})`).join(', ')}
            {dropped.length > 8 && ` …y ${dropped.length - 8} más`}
          </div>
        </div>
      )}
    </div>
  );
};

const IssueList = ({ id, title, hint, items }) => (
  <div id={id} className="border border-danger/30 bg-danger/5 rounded-xl p-4">
    <div className="flex items-start gap-2 mb-2">
      <AlertTriangle size={16} className="text-danger shrink-0 mt-0.5" />
      <div>
        <div className="text-sm font-bold text-danger">{title}</div>
        <div className="text-[11px] text-ink-muted mt-0.5">{hint}</div>
      </div>
    </div>
    <ul className="text-xs space-y-1 max-h-40 overflow-y-auto pl-6">
      {items.map((it) => (
        <li key={it.key} className="text-ink-muted">
          {it.row != null && <><span className="text-ink-muted/60 tabular-nums">#{it.row}</span>{' '}</>}
          <span className="text-ink font-medium">{it.label}</span>
          <span className="text-danger"> · {it.detail}</span>
        </li>
      ))}
    </ul>
  </div>
);

const ImportSection = ({
  icon: Icon, title, description, templateLabel, onTemplate, onPick,
  fileInputRef, parsed, fileInfo, error, previewKeys, previewLabel, rowBadge,
}) => (
  <div className="border border-slate-200 rounded-xl p-4 bg-slate-50">
    <div className="flex items-start justify-between gap-3 flex-wrap mb-2">
      <div className="flex items-start gap-2">
        <div className="w-8 h-8 rounded-lg bg-primary-soft flex items-center justify-center shrink-0">
          <Icon size={15} className="text-primary-darker" />
        </div>
        <div>
          <div className="text-sm font-bold text-ink">{title}</div>
          <div className="text-xs text-ink-muted max-w-xl">{description}</div>
        </div>
      </div>
      <button
        type="button"
        onClick={onTemplate}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-ink text-white hover:bg-slate-800 transition-colors shrink-0"
      >
        <Download size={12} /> {templateLabel}
      </button>
    </div>
    <div
      onClick={() => fileInputRef.current?.click()}
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => { e.preventDefault(); const f = e.dataTransfer.files?.[0]; if (f) onPick(f); }}
      className="border border-dashed border-slate-300 rounded-lg p-4 text-center bg-white hover:border-primary hover:bg-primary-soft/20 transition-colors cursor-pointer"
    >
      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPT_ATTR}
        className="hidden"
        onChange={(e) => { const f = e.target.files?.[0]; if (f) onPick(f); e.target.value = ''; }}
      />
      <FileSpreadsheet size={20} className="mx-auto text-ink-muted/60 mb-1" />
      {parsed
        ? <div className={`text-sm font-semibold ${parsed.length ? 'text-success' : 'text-danger'}`}>{parsed.length ? '✓' : '✗'} {parsed.length} filas listas</div>
        : <div className="text-xs text-ink-muted">Arrastra el archivo o pulsa para seleccionarlo (.xlsx, .xls, .csv, .tsv o .txt)</div>}
      {fileInfo && <div className="text-[11px] text-ink-muted mt-0.5 truncate">{fileInfo}</div>}
    </div>
    {error && (
      <div className="mt-2 inline-flex items-center gap-1.5 text-xs text-danger">
        <AlertTriangle size={11} /> {error}
      </div>
    )}
    {parsed && parsed.length > 0 && (
      <div className="mt-2">
        <div className="text-[11px] uppercase tracking-wider text-ink-muted font-semibold mb-1">{previewLabel}</div>
        <ul className="text-xs space-y-0.5 max-h-28 overflow-y-auto">
          {parsed.slice(0, 8).map((r, i) => {
            const badge = rowBadge ? rowBadge(r) : null;
            return (
              <li key={i} className="text-ink-muted truncate">
                <span className="text-ink-muted/60">#{r._row}</span>{' '}
                <span className="text-ink font-medium">{previewKeys.map((k) => r[k]).filter(Boolean).join(' · ')}</span>
                {badge && <span className="ml-1 text-warning font-semibold">· {badge}</span>}
              </li>
            );
          })}
          {parsed.length > 8 && <li className="text-ink-muted/60 italic">…y {parsed.length - 8} más</li>}
        </ul>
      </div>
    )}
  </div>
);

const ResultBlock = ({ title, entries, icon: Icon }) => {
  if (entries.length === 0) return null;
  const ok = entries.filter((e) => e.ok && !e.skipped).length;
  const skipped = entries.filter((e) => e.ok && e.skipped).length;
  const fail = entries.filter((e) => !e.ok).length;
  return (
    <div className="bg-white border border-slate-200 rounded-xl mb-3 overflow-hidden">
      <div className="flex items-center justify-between px-4 py-2 bg-slate-50 border-b border-slate-200">
        <div className="inline-flex items-center gap-2">
          <Icon size={13} className="text-primary-darker" />
          <span className="text-sm font-bold text-ink">{title}</span>
        </div>
        <div className="text-xs font-semibold inline-flex items-center gap-3">
          <span className="text-success inline-flex items-center gap-1" title="Creados"><CheckCircle2 size={11} /> {ok}</span>
          {skipped > 0 && <span className="text-ink-muted inline-flex items-center gap-1" title="Omitidos"><Info size={11} /> {skipped}</span>}
          {fail > 0 && <span className="text-danger inline-flex items-center gap-1" title="Con error"><X size={11} /> {fail}</span>}
        </div>
      </div>
      <ul className="divide-y divide-slate-100 max-h-48 overflow-y-auto">
        {entries.map((e, i) => (
          <li key={i} className="flex items-center gap-3 px-4 py-2 text-xs">
            {!e.ok
              ? <AlertTriangle size={13} className="text-danger shrink-0" />
              : e.skipped
                ? <Info size={13} className="text-ink-muted shrink-0" />
                : <CheckCircle2 size={13} className="text-success shrink-0" />}
            <span className="text-ink-muted/60 tabular-nums w-10 shrink-0">#{e.row}</span>
            <span className="font-medium text-ink truncate flex-1">{e.name}</span>
            {e.skipped && <span className="text-xs text-ink-muted italic">{e.skipped}</span>}
            {e.warn && <span className="text-xs text-warning truncate max-w-[40%]" title={e.warn}>{e.warn}</span>}
            {e.err && <span className="text-xs text-danger truncate max-w-[40%]" title={e.err}>{e.err}</span>}
          </li>
        ))}
      </ul>
    </div>
  );
};

export default BulkImportModal;
